import { MenuItem, MenuItemProps, MenuList, Stack } from "@mui/material";
import OpenableInput, { OpenableInputProps } from "../_shared/openable-input";
import { Children, cloneElement, ReactElement, useEffect, useRef, useState } from "react";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

export interface SelectProps extends Omit<OpenableInputProps, "boxChildren" | "popoverChildren"> {
    value?: any,
    onValueChange?: (value: any) => any,
}

export default function Select({
    children,
    value = "",
    onValueChange = () => { },
    ...props
}: SelectProps) {
    const childList = Children.toArray(children) as ReactElement[];

    const selectedChild = childList.find(child => child.props.value === value)?.props?.children;

    const [innerOpen, setInnerOpen] = useState(props.defaultOpen);

    return <OpenableInput
        {...props}
        boxChildren={selectedChild}
        popoverChildren={(open, setOpen) => <MenuList autoFocusItem>
            {childList.map(child => cloneElement<SelectOptionProps>(child, {
                selected: Boolean(open && child.props.value === value),
                onClick: e => {
                    child.props.onClick && child.props.onClick(e);
                    if (e.defaultPrevented) return;
                    if (e.type === "keyup" && (e as any).key === " ") return;
                    onValueChange(child.props.value)
                    setOpen(false, e);
                },
                onKeyDown: e => {
                    child.props.onKeyDown && child.props.onKeyDown(e);
                    if (e.defaultPrevented) return;
                    if (e.key === "Enter") {
                        onValueChange(child.props.value)
                        setOpen(false, e);
                    }
                }
            }))}
        </MenuList>}
        slotProps={{
            ...props.slotProps,
            input: {
                ...props.slotProps?.input,
                endAdornment: <Stack direction={"row"} gap={1} alignItems={"center"}>
                    {!innerOpen ? <ArrowDropDownIcon color="action" /> : <ArrowDropUpIcon color="action" />}
                </Stack>
            }
        }}
        onOpen={e => {
            props.onOpen && props.onOpen(e);
            if (!e.defaultPrevented) setInnerOpen(true);
        }}
        onClose={e => {
            props.onClose && props.onClose(e);
            if (!e.defaultPrevented) setInnerOpen(false);
        }}
    />
}

Select.Option = SelectOption;

interface SelectOptionProps extends Omit<MenuItemProps, "value"> {
    value: any,
}

function SelectOption({
    children,
    selected,
    ...props
}: SelectOptionProps) {
    const ref = useRef<HTMLLIElement>(null!);

    useEffect(() => {
        if (selected) ref.current.focus();
    }, [selected])

    return <MenuItem ref={ref} selected={selected} {...props}>{children}</MenuItem>
}